import { useRef, useState } from 'react';
import { SparklesIcon, TrashIcon } from '@heroicons/react/24/outline';
import type { BoxCoordinates } from '@/types';
import { BoxOverlay } from '@/components/ui/BoxOverlay';
import { PrimaryButton, SectionLabel } from './TryoutStep1Upload';

// ============================================================================
// Refine panel — optional region box + free-text instruction
// ============================================================================

interface TryoutRefinePanelProps {
  imageUrl: string;
  refinesLeft: number;
  isRefining: boolean;
  error: string | null;
  onSubmit: (userPrompt: string, box?: BoxCoordinates) => void;
}

export const TryoutRefinePanel: React.FC<TryoutRefinePanelProps> = ({
  imageUrl,
  refinesLeft,
  isRefining,
  error,
  onSubmit,
}) => {
  const imageRef = useRef<HTMLImageElement>(null);
  const [box, setBox] = useState<BoxCoordinates | null>(null);
  const [text, setText] = useState('');

  const noRefinesLeft = refinesLeft <= 0;
  const submitDisabled = isRefining || noRefinesLeft || text.trim().length === 0;

  const handleSubmit = () => {
    if (submitDisabled) return;
    onSubmit(text.trim(), box ?? undefined);
  };

  return (
    <div className="space-y-3" style={{ fontFamily: "'Outfit Variable', 'Outfit', sans-serif" }}>
      <SectionLabel icon={<SparklesIcon className="w-[18px] h-[18px]" strokeWidth={1.5} />}>
        Etwas anpassen? (optional Bereich markieren)
      </SectionLabel>

      {/* Empty-room image with optional region marker — height-capped and
          shrink-wrapped so the panel stays compact (the box overlay tracks the
          image's own client rect, so any size stays aligned). */}
      <div className="relative mx-auto w-fit max-w-full rounded-[14px] overflow-hidden border border-[#EBEBE6]">
        <img
          ref={imageRef}
          src={imageUrl}
          alt="Leerer Raum"
          className="block max-h-[42vh] max-w-full w-auto object-contain select-none"
          draggable={false}
          onContextMenu={(e) => e.preventDefault()}
        />
        <BoxOverlay
          imageRef={imageRef}
          box={box}
          onBoxChange={setBox}
          disabled={isRefining}
        />
      </div>

      <div className="flex items-center justify-between">
        <p className="text-[12px] text-[#9CA3AF]">
          Optional — funktioniert auch ohne Markierung
        </p>
        {box && (
          <button
            type="button"
            onClick={() => setBox(null)}
            disabled={isRefining}
            className="inline-flex items-center gap-1 text-[12px] font-semibold text-[#5F6B70] hover:text-[#001116] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <TrashIcon className="w-3.5 h-3.5" strokeWidth={1.75} />
            Markierung entfernen
          </button>
        )}
      </div>

      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={2}
        disabled={isRefining}
        placeholder={'z. B. „Wand etwas heller“ oder „Fleck am Boden entfernen“'}
        className="w-full rounded-[12px] border border-[#EBEBE6] p-3 text-[14px] text-[#001116] focus:border-[#FE6F17] focus:ring-[3px] focus:ring-[#FE6F17]/20 outline-none resize-none disabled:bg-[#F5F4F0] disabled:cursor-not-allowed"
      />

      <PrimaryButton
        onClick={handleSubmit}
        disabled={submitDisabled}
        trailingIcon={<SparklesIcon className="w-5 h-5" strokeWidth={1.75} />}
      >
        {isRefining ? 'Wird angepasst…' : 'Anpassen'}
      </PrimaryButton>

      {error && <p className="text-[12px] text-[#B91C1C] text-center">{error}</p>}

      <p className="text-center text-[12px] text-[#9CA3AF]">
        {noRefinesLeft
          ? 'Keine Anpassungen mehr übrig'
          : `Noch ${refinesLeft} Anpassung${refinesLeft === 1 ? '' : 'en'} übrig`}
      </p>
    </div>
  );
};
